import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  authAPI,
  resetSessionExpiryState,
} from '../config/api';
import { toast } from 'react-hot-toast';

const AuthContext = createContext(null);

// 会话配置 - 采用主流网站的配置
const SESSION_CONFIG = {
  HEARTBEAT_INTERVAL: 5 * 60 * 1000,  // 5分钟心跳检测
  IDLE_WARNING_TIME: 13 * 60 * 1000,  // 13分钟显示警告
  MAX_IDLE_TIME: 15 * 60 * 1000,      // 15分钟最大空闲时间
  ACTIVITY_WRITE_INTERVAL: 2 * 1000,  // 高频活动最多每2秒持久化一次
  SESSION_KEY: 'app_session_active',   // session storage key
  LAST_ACTIVITY_KEY: 'last_activity',  // 最后活动时间key
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [showDefaultPasswordWarning, setShowDefaultPasswordWarning] = useState(false);
  const lastActivityWriteRef = useRef(0);
  const logoutInProgressRef = useRef(false);

  // 更新最后活动时间
  const updateLastActivity = useCallback((force = false) => {
    const now = Date.now();

    if (
      !force &&
      now - lastActivityWriteRef.current < SESSION_CONFIG.ACTIVITY_WRITE_INTERVAL
    ) {
      return;
    }

    lastActivityWriteRef.current = now;
    localStorage.setItem(SESSION_CONFIG.LAST_ACTIVITY_KEY, now.toString());
  }, []);

  // 检查会话是否有效
  const isSessionValid = useCallback(() => {
    // 检查是否有token
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return false;

    // 检查浏览器会话是否活跃（关键：区分刷新和新开浏览器）
    const sessionActive = sessionStorage.getItem(SESSION_CONFIG.SESSION_KEY);
    if (!sessionActive) {
      // 如果sessionStorage中没有会话标记，说明是新开的浏览器
      return false;
    }

    // 检查空闲时间
    const lastActivity = localStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    if (lastActivity) {
      const idleTime = Date.now() - parseInt(lastActivity);
      if (idleTime > SESSION_CONFIG.MAX_IDLE_TIME) {
        return false;
      }
    }

    return true;
  }, []);

  // 标记会话为活跃状态
  const markSessionActive = useCallback(() => {
    sessionStorage.setItem(SESSION_CONFIG.SESSION_KEY, 'true');
    updateLastActivity(true);
  }, [updateLastActivity]);

  // 清理所有会话数据
  const clearSessionData = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    sessionStorage.removeItem(SESSION_CONFIG.SESSION_KEY);
    lastActivityWriteRef.current = 0;
  }, []);

  // 安全退出
  const logout = useCallback((reason = 'manual') => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;

    clearSessionData();
    setUser(null);
    setToken(null);
    setShowIdleWarning(false);
    setShowDefaultPasswordWarning(false);

    // 显示不同的提示信息
    const messages = {
      idle: '长时间无操作，已自动退出登录',
      expired: '会话已过期，请重新登录',
      manual: '已安全退出登录',
      browser_closed: '检测到浏览器重新打开，请重新登录'
    };

    const message = messages[reason] || '已退出登录';
    
    if (reason === 'manual') {
      toast.success(message);
    } else {
      toast.error(message);
    }

    // 跳转到登录页
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, [clearSessionData]);

  // 登录函数
  const login = useCallback(async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      const newToken = response.token;

      logoutInProgressRef.current = false;
      resetSessionExpiryState();
      
      // 设置状态
      setToken(newToken);
      setUser({ username, isAdmin: response.isAdmin === true });
      setShowDefaultPasswordWarning(response.mustChangePassword === true);
      
      // 存储数据
      localStorage.setItem('token', newToken);
      localStorage.setItem('username', username);
      
      // 标记会话活跃
      markSessionActive();
      
      return response;
    } catch (error) {
      clearSessionData();
      throw error;
    }
  }, [markSessionActive, clearSessionData]);

  // 检查空闲时间并显示警告
  const checkIdleTime = useCallback(() => {
    if (!user || !token) return;

    const lastActivity = localStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    if (!lastActivity) return;

    const idleTime = Date.now() - parseInt(lastActivity);
    
    // 如果超过15分钟，自动登出
    if (idleTime > SESSION_CONFIG.MAX_IDLE_TIME) {
      logout('idle');
      return;
    }
    
    // 如果超过13分钟但不到15分钟，显示警告
    if (idleTime > SESSION_CONFIG.IDLE_WARNING_TIME && !showIdleWarning) {
      setShowIdleWarning(true);
    } else if (idleTime <= SESSION_CONFIG.IDLE_WARNING_TIME && showIdleWarning) {
      setShowIdleWarning(false);
    }
  }, [user, token, showIdleWarning, logout]);

  // 延长会话
  const extendSession = useCallback(() => {
    updateLastActivity(true);
    setShowIdleWarning(false);
  }, [updateLastActivity]);

  const dismissDefaultPasswordWarning = useCallback(() => {
    setShowDefaultPasswordWarning(false);
  }, []);

  // 监听自定义 API 实例派发的认证失效事件，统一清理 React 状态并跳转登录页。
  useEffect(() => {
    const handleSessionExpired = () => logout('expired');

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [logout]);

  // 用户活动监听
  useEffect(() => {
    if (!user || !token) return;

    const handleActivity = () => {
      updateLastActivity();
      if (showIdleWarning) {
        setShowIdleWarning(false);
      }
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user, token, showIdleWarning, updateLastActivity]);

  // 定时检查空闲时间
  useEffect(() => {
    if (!user || !token) return;

    const interval = setInterval(checkIdleTime, 30000); // 每30秒检查一次
    return () => clearInterval(interval);
  }, [user, token, checkIdleTime]);

  // 心跳检测 - 定期验证token有效性
  useEffect(() => {
    if (!user || !token) return;

    const heartbeat = async () => {
      try {
        await authAPI.verifyAuth();
        updateLastActivity(true);
      } catch {
        // 401 由 api 响应拦截器统一处理，避免重复退出和重复提示。
      }
    };

    // 立即执行一次心跳检测
    heartbeat();
    
    // 然后每5分钟执行一次
    const interval = setInterval(heartbeat, SESSION_CONFIG.HEARTBEAT_INTERVAL);
    return () => clearInterval(interval);
  }, [user, token, updateLastActivity, logout]);

  // 初始化检查
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUsername = localStorage.getItem('username');

        if (!storedToken || !storedUsername) {
          setLoading(false);
          return;
        }

        // 检查会话是否有效
        if (!isSessionValid()) {
          clearSessionData();
          // 移除浏览器重新打开的提示，静默处理
          setLoading(false);
          return;
        }

        // 恢复会话状态
        setToken(storedToken);
        setUser({ username: storedUsername });

        // 验证token有效性
        try {
          const verifiedUser = await authAPI.verifyAuth();
          setUser({
            username: verifiedUser.username || storedUsername,
            isAdmin: verifiedUser.isAdmin === true,
          });
          updateLastActivity(true);
        } catch (error) {
          clearSessionData();
          setUser(null);
          setToken(null);
          setShowIdleWarning(false);
          setShowDefaultPasswordWarning(false);

          if (!error.isSessionExpired) {
            toast.error('登录状态验证失败，请重新登录');
          }
        }
      } catch (error) {
        clearSessionData();
        setUser(null);
        setToken(null);
        setShowIdleWarning(false);
        setShowDefaultPasswordWarning(false);
        console.error('初始化认证失败:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [isSessionValid, clearSessionData, updateLastActivity]);

  // 修改密码
  const updatePassword = useCallback(async (currentPassword, newPassword) => {
    if (!user || !token) throw new Error('未登录');
    const response = await authAPI.changePassword(user.username, currentPassword, newPassword);
    if (response?.success) {
      setShowDefaultPasswordWarning(false);
    }
    return response;
  }, [user, token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    showIdleWarning,
    showDefaultPasswordWarning,
    login,
    logout,
    extendSession,
    dismissDefaultPasswordWarning,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
